import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useState, useMemo } from 'react';
import { ForceActionOnClickContext } from '../hooks/use-clickable.js';

/** Default DialogOnCloseOpts that can be used if the user does not pass them in. */
const DEFAULT_DIALOG_ON_CLOSE_OPTS = { source: 'other' };
/**
 * Context that provides a `close` function to the dialog's children.
 */
const DialogContext = createContext(null);
/**
 * Get a `close` function that can be used to create a reusable "Close" or "Cancel" button inside
 * an `ActionStrip`. The close function will be the same one passed to the Dialog's root element.
 *
 * The options object containing the `source` will be optional here, and defaulted to a sane value
 * up to the particular dialog in use, typically `'other'`.
 */
function useClose() {
    const { close } = useContext(DialogContext);
    return close;
}
const DialogCustomCloseContext = createContext({
    customClose: null,
    registerCustomClose: () => { },
});
/**
 * Components that wrap dialog-common components and provide a close button by default can use this
 * context to allow consumers to provide a custom close button. When a custom close button is
 * provided, the default close button should not be rendered.
 */
// eslint-disable-next-line react/display-name
function DialogCustomCloseProvider(props) {
    const [customClose, registerCustomClose] = useState(null);
    const value = useMemo(() => ({ customClose, registerCustomClose }), [customClose]);
    return jsx(DialogCustomCloseContext.Provider, { value: value, ...props });
}
/** Check if the subtree of a `DialogCustomCloseProvider` contains a custom close button. */
function useHasCustomClose() {
    return useContext(DialogCustomCloseContext).customClose != null;
}
/** Used to register a custom close button. */
function useRegisterCustomClose() {
    return useContext(DialogCustomCloseContext).registerCustomClose;
}
const DialogSectionForceActionOnClickContext = createContext({});
/** Provide per-section `ForceActionOnClickContext` overrides to the dialog's sections. */
const DialogSectionForceActionOnClickProvider = DialogSectionForceActionOnClickContext.Provider;
/**
 * Resolve whether a given dialog section should force its clickable elements to act on `click`
 * instead of `pointerdown`. Falls back to the surrounding `ForceActionOnClickContext` when the
 * section has no explicit override.
 */
function useSectionForceActionOnClick(section) {
    const overrides = useContext(DialogSectionForceActionOnClickContext);
    const inherited = useContext(ForceActionOnClickContext);
    return overrides[section] ?? inherited;
}

export { DEFAULT_DIALOG_ON_CLOSE_OPTS, DialogContext, DialogCustomCloseProvider, DialogSectionForceActionOnClickProvider, useClose, useHasCustomClose, useRegisterCustomClose, useSectionForceActionOnClick };
//# sourceMappingURL=dialog-common.context.js.map
